/**
 * Acciones del taller.
 *
 * El módulo tenía cuatro operaciones en la API y la pantalla sólo miraba:
 * un equipo entraba en el taller y no había forma de avanzarlo, de anotar lo
 * que se le había hecho, ni de dar por recuperado el provisional.
 *
 * Lo segundo importa más de lo que parece. Mientras el provisional sigue
 * instalado, el centro tiene un equipo que no es el suyo y el taller tiene
 * uno que ya nadie reclama: es la fuga clásica del sector, y el sistema la
 * detecta pero no ofrecía dónde cerrarla.
 */

import { useCallback, useEffect, useState } from 'react';
import { hasPermission, useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';
import { FormSection, SelectField, TextField } from '../forms/fields.js';

export interface WorkshopOrderRef {
  id: string;
  number: string;
  internal_code: string;
  status: string;
  customer_name: string;
  site_name: string;
}

interface Recovery {
  loanId: string;
  provisionalCode: string;
  customerName: string;
  siteName: string;
  locationPath: string | null;
  installedOn: string;
  daysInstalled?: number;
  urgency?: string;
}

/** Los trece estados del circuito, en el orden en que ocurren. */
const STATUSES = [
  'INSTALADO',
  'RETIRADO',
  'EN_VEHICULO',
  'RECIBIDO',
  'PENDIENTE_DIAGNOSTICO',
  'PENDIENTE_REPARACION',
  'PENDIENTE_MATERIAL',
  'EN_REPARACION',
  'REPARADO',
  'LISTO_ENTREGA',
  'EN_TRANSPORTE',
  'REINSTALADO',
  'SUSTITUIDO',
  'BAJA',
].map((s) => ({ value: s, label: s.replace(/_/g, ' ').toLowerCase() }));

const OPERATIONS = [
  'DIAGNOSTICO',
  'RETIMBRADO',
  'RECARGA',
  'SUSTITUCION_PIEZA',
  'PRUEBA_PRESION',
  'LIMPIEZA',
  'PINTURA',
  'VERIFICACION_FINAL',
  'OTRAS',
].map((s) => ({ value: s, label: s.replace(/_/g, ' ').toLowerCase() }));

/**
 * Lo que se sustituye o se repone en un extintor.
 *
 * Nitrógeno, polvo y presurizado no son piezas, pero se anotan aquí: es lo
 * que la ficha del acta tiene que enumerar, y el técnico no distingue —ni
 * tiene por qué— entre «cambiar» y «reponer» al rellenar el parte.
 */
const PARTS = [
  { value: 'MANETA', label: 'Maneta' },
  { value: 'ANILLA', label: 'Anilla' },
  { value: 'MANOMETRO', label: 'Manómetro' },
  { value: 'JUNTA', label: 'Junta' },
  { value: 'VALVULA', label: 'Válvula' },
  { value: 'MANGUERA', label: 'Manguera' },
  { value: 'SOPORTE', label: 'Soporte' },
  { value: 'ETIQUETA', label: 'Etiqueta' },
  { value: 'NITROGENO', label: 'Nitrógeno' },
  { value: 'POLVO', label: 'Polvo' },
  { value: 'PRESURIZADO', label: 'Presurizado' },
  { value: 'OTRA', label: 'Otra' },
];

const RESULTS = [
  { value: 'CORRECTO', label: 'Correcto' },
  { value: 'NO_CONFORME', label: 'No conforme' },
  { value: 'NO_REPARABLE', label: 'No reparable' },
];

export function WorkshopActions({
  order,
  onDone,
}: {
  order: WorkshopOrderRef | null;
  onDone: () => void;
}): React.ReactNode {
  const { api, user } = useSession();

  const [recoveries, setRecoveries] = useState<Recovery[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [status, setStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [operation, setOperation] = useState('DIAGNOSTICO');
  const [description, setDescription] = useState('');
  const [result, setResult] = useState('');
  const [minutes, setMinutes] = useState('');
  const [part, setPart] = useState('');
  const [weightBefore, setWeightBefore] = useState('');
  const [weightAfter, setWeightAfter] = useState('');

  const canRepair = hasPermission(user, 'workshop.repair');
  const canDispatch = hasPermission(user, 'workshop.dispatch');

  const loadRecoveries = useCallback(async () => {
    const data = await api
      .get<Recovery[] | { items: Recovery[] }>('/workshop/provisional-recoveries')
      .catch(() => [] as Recovery[]);
    setRecoveries(Array.isArray(data) ? data : (data.items ?? []));
  }, [api]);

  useEffect(() => {
    void loadRecoveries();
  }, [loadRecoveries]);

  async function changeStatus(): Promise<void> {
    if (!order || status === '') return;
    setError(null);
    setBusy(true);
    try {
      await api.post(`/workshop/${order.id}/status`, {
        status,
        ...(statusReason === '' ? {} : { reason: statusReason }),
      });
      setNotice(`${order.internal_code}: estado cambiado a ${status.replace(/_/g, ' ').toLowerCase()}.`);
      setStatus('');
      setStatusReason('');
      onDone();
    } catch (caught) {
      // El servidor valida qué transiciones son legales: un equipo no pasa
      // de RECIBIDO a REINSTALADO sin pasar por reparación. Si la rechaza,
      // se enseña su motivo tal cual, que es más útil que uno genérico.
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido cambiar el estado.');
    } finally {
      setBusy(false);
    }
  }

  async function addOperation(): Promise<void> {
    if (!order) return;
    setError(null);
    setBusy(true);
    try {
      await api.post(`/workshop/${order.id}/operations`, {
        operation,
        ...(description === '' ? {} : { description }),
        ...(result === '' ? {} : { result }),
        ...(minutes === '' ? {} : { minutesSpent: Number(minutes) }),
        ...(part === '' ? {} : { part }),
        ...(weightBefore === '' ? {} : { weightBeforeKg: weightBefore }),
        ...(weightAfter === '' ? {} : { weightAfterKg: weightAfter }),
      });
      setNotice(`${order.internal_code}: operación registrada.`);
      setDescription('');
      setResult('');
      setMinutes('');
      setPart('');
      setWeightBefore('');
      setWeightAfter('');
      onDone();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido registrar la operación.');
    } finally {
      setBusy(false);
    }
  }

  async function recover(loan: Recovery): Promise<void> {
    if (
      !globalThis.confirm(
        `¿Dar por recuperado el provisional ${loan.provisionalCode}?\n\n` +
          `Está instalado en ${loan.siteName} desde ${loan.installedOn}.\n` +
          'Confirme que el equipo original ya está de vuelta en su sitio.',
      )
    ) {
      return;
    }

    setError(null);
    setBusy(true);
    try {
      await api.post(`/workshop/provisional-loans/${loan.loanId}/recover`, {});
      setNotice(`Provisional ${loan.provisionalCode} recuperado.`);
      await loadRecoveries();
      onDone();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido recuperar el provisional.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error !== null && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="screen__note screen__note--strong" role="status">
          {notice}
        </p>
      )}

      {recoveries.length > 0 && (
        <section className="panel">
          <h3 className="panel__title">Provisionales pendientes de recuperar ({recoveries.length})</h3>
          <p className="screen__note">
            Cada uno es un equipo vuestro instalado en casa de un cliente. Si nadie lo recupera, se
            queda ahí: es la fuga más común del circuito de taller.
          </p>
          <ul className="attachments">
            {recoveries.map((loan) => (
              <li className="attachments__item" key={loan.loanId}>
                <span className="attachments__name">
                  {loan.provisionalCode} · {loan.customerName} · {loan.siteName}
                  {loan.locationPath ? ` · ${loan.locationPath}` : ''}
                </span>
                <span className="attachments__meta">
                  desde {loan.installedOn}
                  {loan.daysInstalled !== undefined ? ` (${loan.daysInstalled} días)` : ''}
                </span>
                <button
                  className="button button--quiet"
                  type="button"
                  disabled={busy || !canDispatch}
                  onClick={() => void recover(loan)}
                >
                  Recuperado
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {order !== null && (
        <section className="panel">
          <h3 className="panel__title">
            {order.internal_code} · {order.customer_name} · {order.site_name}
          </h3>
          <p className="screen__note">
            Orden {order.number} · estado actual{' '}
            <strong>{order.status.replace(/_/g, ' ').toLowerCase()}</strong>
          </p>

          <div className="form">
            <FormSection title="Cambiar de estado">
              <SelectField
                label="Nuevo estado"
                value={status}
                onChange={setStatus}
                options={STATUSES}
                emptyLabel="Seleccione…"
                help="El servidor rechaza las transiciones que el circuito no permite."
              />
              <TextField label="Motivo" value={statusReason} onChange={setStatusReason} />
              <button
                className="button button--primary"
                type="button"
                disabled={busy || status === '' || !canDispatch}
                onClick={() => void changeStatus()}
              >
                {busy ? 'Guardando…' : 'Cambiar estado'}
              </button>
            </FormSection>

            <FormSection title="Registrar operación">
              <SelectField
                label="Operación"
                value={operation}
                onChange={setOperation}
                options={OPERATIONS}
              />
              <TextField label="Descripción" value={description} onChange={setDescription} />
              <SelectField
                label="Pieza cambiada o repuesta"
                value={part}
                onChange={setPart}
                options={PARTS}
                emptyLabel="Ninguna"
                help={
                  operation === 'SUSTITUCION_PIEZA'
                    ? 'Obligatoria: el acta tiene que decir qué se cambió.'
                    : 'Aparecerá en la ficha del equipo en el acta.'
                }
              />
              <TextField
                label="Peso al entrar (kg)"
                value={weightBefore}
                onChange={setWeightBefore}
                help="Con el de salida acredita la recarga. Los dos o ninguno."
              />
              <TextField label="Peso al salir (kg)" value={weightAfter} onChange={setWeightAfter} />
              <SelectField
                label="Resultado"
                value={result}
                onChange={setResult}
                options={RESULTS}
                emptyLabel="Sin determinar"
              />
              <TextField
                label="Minutos empleados"
                type="number"
                value={minutes}
                onChange={setMinutes}
                help="Alimenta la rentabilidad del taller."
              />
              <button
                className="button button--primary"
                type="button"
                disabled={busy || !canRepair}
                onClick={() => void addOperation()}
              >
                {busy ? 'Guardando…' : 'Registrar operación'}
              </button>
            </FormSection>
          </div>
        </section>
      )}
    </>
  );
}
